<script lang="ts">
	import { t } from "@hitit/hub/src/stores";
	import sanitizeHtml from "sanitize-html";
	import { DefaultSwitch } from "@hitit/new_ui";
	import StoreImageTitleEditor from "./StoreImageTitleEditor.svelte";
	import StoreNameValue from "./StoreNameValue.svelte";
	import PlainTextEditor from "@hitit/hub/src/components/place/PlainTextEditor.svelte";
	import {
		getNationalPhonenumber,
		addSpacer,
	} from "../../../functions/utils/phone";

	export let storeImageURL = "";
	export let editmode = true;
	export let formattedAddress1 = "";
	export let formattedAddress2 = "";
	export let phonenumber = "";
	export let phonenumberCountryCode = "";
	export let cacheReceiptAvailable = "";
	export let type1 = "";
	export let type2 = "";
	export let detailAddress = "";
	export let appLockEnabled = false;
	export let storeDescription = "";
	export let storename = "";
	export let storeStatus = "";
	export let smartpayAvailable = "";

	const lengthLimit = 2000;
	const lineLimit = 100;
	const textEditorFocusBlurHandler = (bool: boolean) => {
		return () => (editorFocus = bool);
	};
	const storeDescriptionOption = {
		allowedTags: [],
		allowedAttributes: {},
	};

	let editorFocus = false;
	let storePhonenumber = getNationalPhonenumber(
		phonenumber,
		phonenumberCountryCode
	);
	function onInput(e: Event) {
		const target = e.currentTarget as HTMLInputElement;
		const { value } = target;
		const originalCaret = target.selectionEnd;
		const originalGap = value.length - (originalCaret || 0);
		const replace = value
			.replace(/-/g, "") //"-"제거
			.replace(/[^0-9]/g, ""); //숫자를제거한 나머지 모두 제거
		const newValue = addSpacer(replace); //다시 addSpacer
		target.value = newValue;
		target.focus();

		//캐럿을 따로 설정해주지 않으면, target.value = newValue로 인해서 캐럿이 항상 마지막으로 이동한다.
		const newPosition = newValue.length - originalGap;
		target.setSelectionRange(newPosition, newPosition);

		storePhonenumber = replace;
		phonenumber = getNationalPhonenumber(replace);
	}

	function textEditorHandler(e: { detail: { html: string } }) {
		storeDescription = e.detail.html;
	}
</script>

<div class="container" class:editmode={editmode}>
	<div class="top-box">
		<StoreImageTitleEditor
			storeStatus={storeStatus}
			editmode={editmode}
			bind:storeImageURL={storeImageURL}
			bind:storename={storename}
		/>
	</div>
	<div class="store-infomation-container">
		<div id="store-description">
			<StoreNameValue
				name={$t("hub.store.storeinfo.store_desc")}
				value={sanitizeHtml(storeDescription, storeDescriptionOption)}
				editmode={editmode}
			>
				<div
					id="store-description-editor-wrapper"
					slot="input"
					class:editorFocus={editorFocus}
				>
					<PlainTextEditor
						on:UPDATE_TEXT={textEditorHandler}
						value={storeDescription}
						lengthLimit={lengthLimit}
						lineLimit={lineLimit}
						focusEvent={textEditorFocusBlurHandler(true)}
						blurEvent={textEditorFocusBlurHandler(false)}
					/>
				</div>
			</StoreNameValue>
		</div>
		<div class="name-value">
			<StoreNameValue
				clearCallback={() => (storePhonenumber = "")}
				on:input={onInput}
				value={addSpacer(storePhonenumber)}
				name={$t("hub.store.storeinfo.phonenumber")}
				editmode={editmode}
			/>
		</div>
		<div class="name-value dummy" />
		<div class="name-value">
			<StoreNameValue
				name={$t("hub.store.storeinfo.smartpay")}
				value={smartpayAvailable === "TRUE"
					? $t("hub.store.widget.cashreceipt.enabled")
					: $t("hub.store.widget.cashreceipt.disabled")}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				description={$t("hub.store.widget.cashreceipt.tooltip")}
				name={$t("hub.store.widget.cashreceipt.title")}
				value={cacheReceiptAvailable === "TRUE"
					? $t("hub.store.widget.cashreceipt.enabled")
					: $t("hub.store.widget.cashreceipt.disabled")}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				name={$t("hub.store.storeinfo.category1")}
				value={type1}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				name={$t("hub.store.storeinfo.category2")}
				value={type2}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				name={$t("hub.store.storeinfo.address1")}
				value={formattedAddress2 || ""}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				name={$t("hub.store.storeinfo.address2")}
				value={formattedAddress1 || ""}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				name={$t("hub.store.storeinfo.address_detailed")}
				bind:value={detailAddress}
				editmode={editmode}
			/>
		</div>
		<div class="name-value">
			<StoreNameValue
				description={$t("hub.store.storeinfo.Lock_tooltip")}
				name={$t("hub.store.storeinfo.Store_Infomation_Lock")}
				value={appLockEnabled
					? $t("hub.store.widget.cashreceipt.enabled")
					: $t("hub.store.widget.cashreceipt.disabled")}
				editmode={editmode}
			>
				<div slot="input" class="lock-box">
					<DefaultSwitch bind:checked={appLockEnabled} />
				</div>
			</StoreNameValue>
		</div>
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	.container {
		display: flex;
		flex-direction: column;
		gap: 10px;
		.top-box {
			flex-grow: 1;
		}
		.dummy {
			display: none;
		}

		.store-infomation-container {
			display: flex;
			flex-direction: column;
			gap: 20px;
		}

		#store-description {
			width: 100%;
			#store-description-editor-wrapper {
				padding: 10px;
				background-color: #f0f0f4;
				min-height: 150px;
				border-radius: 5px;
				transition: box-shadow 0.1s;

				&:hover {
					cursor: text;
				}

				:global(.plainTextEditor .ql-editor) {
					outline: 0px solid transparent;
					min-height: 150px;
					overflow: auto;
					line-height: 1.375rem;
					color: #46474b;
				}

				:global(.plainTextEditor .ql-editor:focus) {
					outline: 0px solid transparent;
				}

				&.editorFocus {
					box-shadow: 0 0 0 2pt var(--signature-blue);
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			.dummy {
				display: flex;
			}
			.top-box {
				margin-bottom: 30px;
			}

			&.editmode {
				.top-box {
					margin-bottom: 20px;
				}
			}

			&:not(.editmode) {
				background-image: url("/img/common/counter.png");
				background-repeat: no-repeat;
				background-size: 110px 130.5px;
				background-position: bottom right;
			}

			#store-description {
				width: 100%;
				#store-description-editor-wrapper {
					font-size: 1.25rem;
					:global(.plainTextEditor .ql-editor) {
						line-height: 1.875rem;
					}
				}
			}
			.store-infomation-container {
				flex-direction: row;
				gap: 30px 0px;
				flex-wrap: wrap;
				.name-value {
					width: 50%;
					max-width: 50%;
				}
				.lock-box {
					display: flex;
					align-items: center;
					height: 40px;
				}
			}
		}
	}
</style>
